/**
 * Firefox-specific MediaRecorder utilities.
 * Firefox has a known issue where the MediaRecorder's 'onstop' event may not fire reliably:
 * https://bugzilla.mozilla.org/show_bug.cgi?id=1734542
 */
interface MediaRecorderOptions {
	onStop: (recorder: MediaRecorder, videoTrack: MediaStreamTrack, audioTrack: MediaStreamTrack) => Promise<void>;
}
// Firefox does not respond to the onstop event, so we need to listen for the track ended event
// https://developer.mozilla.org/en-US/docs/Web/API/MediaRecorder/stop_event

export const setupFirefoxMediaRecorder = (recorder: MediaRecorder, stream: MediaStream, options: MediaRecorderOptions): { cleanup: () => void } => {
	// Store references to tracks
	const videoTrack = stream.getVideoTracks()[0];
	const audioTrack = stream.getAudioTracks()[0];

	// Set up track ended listeners for Firefox
	const handleTrackEnded = () => {
		if (!videoTrack || !audioTrack) return;
		// Remove the listeners to prevent multiple calls
		videoTrack.removeEventListener('ended', handleTrackEnded);
		audioTrack.removeEventListener('ended', handleTrackEnded);
		options.onStop(recorder, videoTrack, audioTrack);
	};

	// Add track ended listeners
	videoTrack?.addEventListener('ended', handleTrackEnded);
	audioTrack?.addEventListener('ended', handleTrackEnded);

	// Set up the onstop handler
	recorder.onstop = async () => {
		if (!videoTrack || !audioTrack) return;
		// Remove track ended listeners if onstop fires
		videoTrack.removeEventListener('ended', handleTrackEnded);
		audioTrack.removeEventListener('ended', handleTrackEnded);
		await options.onStop(recorder, videoTrack, audioTrack);
	};

	return {
		cleanup: () => {
			videoTrack?.removeEventListener('ended', handleTrackEnded);
			audioTrack?.removeEventListener('ended', handleTrackEnded);
		},
	};
};
